import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ArrowLeft, Camera, Check, ChevronLeft, ChevronRight, Images, Share2, X } from "lucide-react";
import "./shopGallery.css";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import useModalDismiss from "../useModalDismiss";

const getImageUrl = (img) => {
	if (!img) return "";
	return img.startsWith("http") ? img : `https://${process.env.REACT_APP_IMAGES_BUCKET}.s3.${process.env.REACT_APP_APP_REGION}.amazonaws.com/${img}`;
};

/**
 * Shop photo gallery, shared by the shop page and the staff page.
 * One markup tree, two presentations: a swipeable carousel on phones,
 * an editorial mosaic from 900px up when there are 3+ photos.
 *
 * `shareUrl` is the absolute URL the share button copies/shares.
 */
export default function ShopGallery({ images = [], shareUrl = "", onBack }) {
	const trackRef = useRef(null);
	const [idx, setIdx] = useState(0);
	const [viewerOpen, setViewerOpen] = useState(false);
	const [copied, setCopied] = useState(false);

	const dragging = useRef(false);
	const moved = useRef(false);
	const startX = useRef(0);
	const deltaX = useRef(0);

	const { language } = useContext(CombinedContext);
	const copy = translations[language].shopPage.gallery;

	const total = images.length;
	const isMosaic = total >= 3;

	const goTo = useCallback((n) => setIdx(((n % total) + total) % total), [total]);
	const next = useCallback(() => goTo(idx + 1), [goTo, idx]);
	const prev = useCallback(() => goTo(idx - 1), [goTo, idx]);

	const viewerDismiss = useModalDismiss(() => setViewerOpen(false), { dismissOnBackdrop: true, enabled: viewerOpen });

	useEffect(() => {
		if (!total) return undefined;
		const onKey = (e) => {
			if (e.key === "ArrowRight") next();
			if (e.key === "ArrowLeft") prev();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [next, prev, total]);

	useEffect(() => {
		if (!viewerOpen) return undefined;
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previous;
		};
	}, [viewerOpen]);

	useEffect(() => {
		if (!trackRef.current) return;
		trackRef.current.style.transition = "transform 340ms cubic-bezier(0.22, 0.61, 0.36, 1)";
		trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
	}, [idx]);

	const onDragStart = (e) => {
		dragging.current = true;
		moved.current = false;
		startX.current = e.touches ? e.touches[0].clientX : e.clientX;
		deltaX.current = 0;
	};

	const onDragMove = (e) => {
		if (!dragging.current) return;
		const x = e.touches ? e.touches[0].clientX : e.clientX;
		deltaX.current = x - startX.current;
		if (Math.abs(deltaX.current) > 6) moved.current = true;
		if (trackRef.current) {
			trackRef.current.style.transition = "none";
			trackRef.current.style.transform = `translateX(calc(${-idx * 100}% + ${deltaX.current}px))`;
		}
	};

	const onDragEnd = () => {
		if (!dragging.current) return;
		dragging.current = false;
		if (Math.abs(deltaX.current) > 60) {
			if (deltaX.current < 0) next();
			else prev();
		}
		if (trackRef.current) {
			trackRef.current.style.transition = "";
			trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
		}
		deltaX.current = 0;
	};

	const openViewerAt = (i) => {
		if (moved.current) return;
		setIdx(i);
		setViewerOpen(true);
	};

	const onShare = async () => {
		if (!shareUrl) return;
		if (navigator.share) {
			try {
				await navigator.share({ url: shareUrl });
			} catch {
				/* user dismissed the share sheet */
			}
			return;
		}
		try {
			await navigator.clipboard.writeText(shareUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2200);
		} catch {
			/* clipboard unavailable */
		}
	};

	return (
		<>
			<div
				className={`sf-shop-gallery ${isMosaic ? "is-mosaic" : ""} ${total === 0 ? "is-empty" : ""}`}
				data-count={Math.min(total, 5)}
				aria-roledescription="carousel"
			>
				<div className="sf-shop-gallery-bar">
					{/* the admin card renders its own page chrome, so back is optional */}
					{onBack ? (
						<button type="button" className="sf-shop-glass-btn" onClick={onBack} aria-label={copy.back}>
							<ArrowLeft size={18} />
						</button>
					) : (
						<span />
					)}
					<button type="button" className={`sf-shop-glass-btn ${copied ? "is-done" : ""}`} onClick={onShare} aria-label={copy.share}>
						{copied ? <Check size={18} /> : <Share2 size={18} />}
						{copied && <span className="sf-shop-glass-toast">{copy.linkCopied}</span>}
					</button>
				</div>

				{total === 0 ? (
					<div className="sf-shop-gallery-empty">
						<Camera size={30} strokeWidth={1.4} />
						<span>{copy.noPhotos}</span>
					</div>
				) : (
					<>
						<div
							className="sf-shop-gallery-viewport"
							onTouchStart={onDragStart}
							onTouchMove={onDragMove}
							onTouchEnd={onDragEnd}
							onMouseDown={(e) => {
								e.preventDefault();
								onDragStart(e);
								window.addEventListener("mousemove", onDragMove);
								window.addEventListener(
									"mouseup",
									() => {
										onDragEnd();
										window.removeEventListener("mousemove", onDragMove);
									},
									{ once: true },
								);
							}}
						>
							<div className="sf-shop-gallery-track" ref={trackRef}>
								{images.map((img, i) => (
									<button
										type="button"
										className="sf-shop-gallery-slide"
										key={`${img}-${i}`}
										aria-hidden={!isMosaic && i !== idx}
										onClick={() => openViewerAt(i)}
										aria-label={`${copy.photo} ${i + 1}`}
									>
										<img className="sf-shop-gallery-img" src={getImageUrl(img)} alt="" draggable={false} loading={i === 0 ? "eager" : "lazy"} />
									</button>
								))}
							</div>

							{total > 1 && (
								<>
									<button type="button" className="sf-shop-gallery-arrow is-left" onClick={prev} aria-label={copy.previous}>
										<ChevronLeft size={20} />
									</button>
									<button type="button" className="sf-shop-gallery-arrow is-right" onClick={next} aria-label={copy.next}>
										<ChevronRight size={20} />
									</button>
								</>
							)}
						</div>

						<button type="button" className="sf-shop-gallery-all" onClick={() => setViewerOpen(true)}>
							<Images size={15} />
							{total} {total === 1 ? copy.photosOne : copy.photos}
						</button>

						{total > 1 && (
							<div className="sf-shop-gallery-dots">
								{images.map((_, i) => (
									<button
										type="button"
										key={i}
										className={`sf-shop-gallery-dot ${i === idx ? "is-active" : ""}`}
										onClick={() => goTo(i)}
										aria-label={`${copy.photo} ${i + 1}`}
									/>
								))}
							</div>
						)}
					</>
				)}
			</div>

			{viewerOpen && total > 0 && (
				<div className="sf-shop-viewer" role="dialog" aria-modal="true" {...viewerDismiss}>
					<button type="button" className="sf-shop-viewer-close" onClick={() => setViewerOpen(false)} aria-label={copy.close}>
						<X size={20} />
					</button>
					<span className="sf-shop-viewer-count">
						{idx + 1} / {total}
					</span>
					<img className="sf-shop-viewer-img" src={getImageUrl(images[idx])} alt="" onClick={(e) => e.stopPropagation()} />
					{total > 1 && (
						<>
							<button
								type="button"
								className="sf-shop-viewer-arrow is-left"
								onClick={(e) => {
									e.stopPropagation();
									prev();
								}}
								aria-label={copy.previous}
							>
								<ChevronLeft size={26} />
							</button>
							<button
								type="button"
								className="sf-shop-viewer-arrow is-right"
								onClick={(e) => {
									e.stopPropagation();
									next();
								}}
								aria-label={copy.next}
							>
								<ChevronRight size={26} />
							</button>
						</>
					)}
				</div>
			)}
		</>
	);
}
